import React, { useCallback, useEffect, useState } from 'react';

// ROUTES
import { useHistory } from 'react-router-dom';
import { ROUTE_DETRAN_CNH_PERMISSIONARIO_PONTUADO_AGENDAMENTO_MEDICO } from 'pages/DetranCnh/routes/paths';
import { ROUTE_DETRAN_CNH } from 'routes/paths';

// ANTD
import { Row, Col } from 'antd';

// COMPONENTS COMMON
import ButtonVoltar from 'components/Common/ButtonVoltar';
import ButtonImage from 'components/Common/ButtonImage';

// COMPONENTS
import StatusCondutor from 'pages/DetranCnh/components/pages/PreCadastro/StatusCondutor';

// REDUX
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { DadosCidadaoRequest } from 'store/modules/api/detranCnh/divisaoEquitativa/dadosCidadao/types';
import {
	cadastroEnderecosClear,
	cadastroEnderecosRequest,
} from 'store/modules/api/detranCnh/condutor/cadastroEnderecos/actions';
import {
	dadosCidadaoClear,
	dadosCidadaoRequest,
} from 'store/modules/api/detranCnh/divisaoEquitativa/dadosCidadao/actions';
import { GerarRenachForm } from 'store/modules/api/detranCnh/sockets/gerarRenach/types';
import {
	consultaNomeSocialFiliacaoClear,
	consultaNomeSocialFiliacaoRequest,
} from 'store/modules/api/detranCnh/condutor/consultaNomeSocialFiliacao/actions';
import { consultaCategoriaRequest } from 'store/modules/api/detranCnh/consulta/categoria/actions';
import {
	consultaPesquisaNomeSocialFiliacaoClear,
	consultaPesquisaNomeSocialFiliacaoRequest,
} from 'store/modules/api/detranCnh/IIRGDDetran/consultaPesquisaNomeSocialFiliacao/actions';
import {
	consultaSegundaViaReabilitacaoClear,
	consultaSegundaViaReabilitacaoRequest,
} from 'store/modules/api/detranCnh/reabilitacao/consultaSegundaViaReabilitacao/actions';
import {
	consultaCEPClear,
	consultaCEPRequest,
} from 'store/modules/api/detranCnh/consulta/cep/actions';
import {
	domainServiceNaturalidadesClear,
	domainServiceNaturalidadesRequest,
} from 'store/modules/api/sguService/domainService/naturalidade/actions';
import { consultaCidadesUFRequest } from 'store/modules/api/utils/consultaCidadesUF/actions';
import { saveGerarRenachRequest } from 'store/modules/api/detranCnh/sockets/saveGerarRenach/actions';
import {
	consultarInaptoClear,
	consultarInaptoRequest,
} from 'store/modules/api/detranCnh/sockets/consultarInapto/actions';
import { GerarRenachIntegradoRequest } from 'store/modules/api/detranCnh/sockets/saveGerarRenach/types';
import { dadosPreCadastroFormSaveRequest } from 'store/modules/api/detranCnh/utils/dadosPreCadastroFormSave/actions';
import { gerarRenachIntegradoClear } from 'store/modules/api/detranCnh/renachIntegrado/gerarRenachIntegrado/actions';
import { estatisticaIncluirClear } from 'store/modules/api/sguService/estatisticasIncluir/actions';
import { addNotifications } from 'store/modules/app/notifications/actions';

// UTILS
import { limparMascara } from 'utils/genericFunctions';
import { getOriginOfSocialName } from 'pages/DetranCnh/utils/functions/socialName';
import { treatValuesCadastroEnderecoRequest } from 'pages/DetranCnh/utils/threatServicesRequest';
import { CNH_PROCESS_VALIDATION } from 'pages/DetranCnh/utils/constants/messages';
import { getEventoConsultar, SguData } from 'utils/eventBuilder';

// FORM
import { treatValues, treatInitialValues, schema } from './form';

const StatusCondutorPermissionarioPontuado: React.FC = () => {
	const history = useHistory();
	const dispatch = useDispatch();

	const { consultaPermissionarioPontuado } = useSelector(
		(state: ApplicationState) => state.api.detranCnh.eportal,
	);

	const { user, mainframe } = useSelector(
		(state: ApplicationState) => state.api.sgu.loginUnico.login,
	);

	const { cadastroEnderecos, consultaNomeSocialFiliacao } = useSelector(
		(state: ApplicationState) => state.api.detranCnh.condutor,
	);

	const { consultaPesquisaNomeSocialFiliacao } = useSelector(
		(state: ApplicationState) => state.api.detranCnh.IIRGDDetran,
	);

	const { dadosCidadao } = useSelector(
		(state: ApplicationState) => state.api.detranCnh.divisaoEquitativa,
	);

	const { consultaCEP } = useSelector(
		(state: ApplicationState) => state.api.detranCnh.consulta,
	);

	const { consultaSegundaViaReabilitacao } = useSelector(
		(state: ApplicationState) => state.api.detranCnh.reabilitacao,
	);

	const { consultaNaturalidades } = useSelector(
		(state: ApplicationState) => state.api.sguService.domainService,
	);

	const { consultarInapto } = useSelector(
		(state: ApplicationState) => state.api.detranCnh.sockets,
	);

	const { dadosPreCadastroFormSave } = useSelector(
		(state: ApplicationState) => state.api.detranCnh.utils,
	);

	// CONTABILIZAÇÃO
	const { sgu } = useSelector((state: ApplicationState) => state.api);

	const [dadosPreCadastro, setDadosPreCadastro] = useState(
		treatInitialValues(null, null, null).initialValuesPreCadastro,
	);
	const [dadosHabilitacao, setDadosHabilitacao] = useState(
		treatInitialValues(null, null, null).initalValuesHabilitacao,
	);
	const [requestData, setRequestData] = useState<null | {
		renach: GerarRenachIntegradoRequest;
		cidadao: DadosCidadaoRequest;
	}>(null);

	const handleSubmit = useCallback(
		(values: GerarRenachForm) => {
			if (!values.codCiretranCondutor || !values.codCiretranHabilitacao) {
				addNotifications({
					errors: CNH_PROCESS_VALIDATION.CIRETRAN_INVALIDA,
				});
				return;
			}

			const sguData: SguData = {
				salvarAtendimento: sgu.atendimento.salvarAtendimento,
				login: sgu.loginUnico.login,
			};
			const evento = getEventoConsultar(sguData, values.cpf);

			const renach = treatValues(evento, values, user);
			const endereco = treatValuesCadastroEnderecoRequest(values);

			const cidadao: DadosCidadaoRequest = {
				cidadao: {
					autoriza_envio_email: values.autorizaSMS === 'S',
					autoriza_envio_sms: values.autorizaEmail === 'S',
					cpf: Number(values.cpf),
					celular: limparMascara(values.celular).substring(2, 11),
					ddd_celular: limparMascara(values.celular).substring(0, 2),
					ddd_telefone: limparMascara(values.telefone).substring(0, 2),
					telefone: limparMascara(values.telefone).substring(2, 11),
					email: values.email || '',
					necessidade_veiculo_adaptado: false,
					nome: values.nomeSocial?.toUpperCase() || values.nome.toUpperCase(),
					pretende_atividade_remunerada: values.flagAtividadeRemunerada === 'S',
				},
			};

			setDadosPreCadastro(values);
			setRequestData({ renach, cidadao });

			dispatch(saveGerarRenachRequest(renach));
			dispatch(cadastroEnderecosRequest(endereco));

			// SALVA OS DADOS PREENCHIDOS NO FORM.
			dispatch(
				dadosPreCadastroFormSaveRequest({
					dataPreCadastro: values,
					dataPreCadastroHabilitacao: dadosHabilitacao,
				}),
			);
			dispatch(gerarRenachIntegradoClear());
		},
		[dadosHabilitacao, dispatch, sgu, user],
	);

	const clearInitialStatesWithoutNewCallServices = useCallback(() => {
		dispatch(consultaCategoriaRequest({ funcionalidade: 'PERMISSIONARIO' }));
		dispatch(cadastroEnderecosClear());
		dispatch(estatisticaIncluirClear());
		dispatch(consultarInaptoClear());
		dispatch(dadosCidadaoClear());
	}, [dispatch]);

	useEffect(() => {
		if (
			consultaPermissionarioPontuado.data &&
			consultaPermissionarioPontuado.data.cpf &&
			consultaSegundaViaReabilitacao.status === 0
		) {
			dispatch(
				consultaSegundaViaReabilitacaoRequest({
					cpfCidadao: consultaPermissionarioPontuado.data.cpf,
				}),
			);
		}
	}, [
		consultaPermissionarioPontuado.data,
		consultaPermissionarioPontuado.status,
		consultaSegundaViaReabilitacao.status,
		dispatch,
	]);

	useEffect(() => {
		if (
			consultaPermissionarioPontuado.data &&
			consultaSegundaViaReabilitacao.status === 200 &&
			consultaSegundaViaReabilitacao.data &&
			user &&
			(!dadosPreCadastroFormSave.dataPreCadastro?.cpf ||
				!dadosPreCadastroFormSave.dataPreCadastroHabilitacao?.cpf)
		) {
			const newFormValues = treatInitialValues(
				consultaPermissionarioPontuado.data,
				consultaSegundaViaReabilitacao.data.dataPrimeiraHabilitacao || null,
				user,
			);

			setDadosPreCadastro(newFormValues.initialValuesPreCadastro);
			setDadosHabilitacao(newFormValues.initalValuesHabilitacao);
		}
	}, [
		consultaPermissionarioPontuado.data,
		consultaSegundaViaReabilitacao.status,
		consultaSegundaViaReabilitacao.data,
		user,
		dadosPreCadastroFormSave,
	]);

	// GET UF
	useEffect(() => {
		if (
			consultaSegundaViaReabilitacao.status === 200 &&
			consultaSegundaViaReabilitacao.data &&
			consultaSegundaViaReabilitacao.data.naturalidade &&
			consultaSegundaViaReabilitacao.data.nacionalidade?.toString() === '1' &&
			dadosPreCadastro.cpf &&
			dadosPreCadastro.cpf !== '' &&
			(!dadosPreCadastroFormSave.dataPreCadastro?.cpf ||
				!dadosPreCadastroFormSave.dataPreCadastroHabilitacao?.cpf)
		) {
			dispatch(
				domainServiceNaturalidadesRequest({
					idIbge: consultaSegundaViaReabilitacao.data.naturalidade.trim(),
				}),
			);
		}
	}, [
		consultaSegundaViaReabilitacao.data,
		consultaSegundaViaReabilitacao.status,
		dadosPreCadastro.cpf,
		dispatch,
		dadosPreCadastroFormSave,
	]);

	useEffect(() => {
		if (
			consultaNaturalidades.status === 200 &&
			consultaNaturalidades.data &&
			consultaNaturalidades.data[0].municipioIBGE?.uf &&
			(!dadosPreCadastroFormSave.dataPreCadastro?.cpf ||
				!dadosPreCadastroFormSave.dataPreCadastroHabilitacao?.cpf)
		) {
			setDadosHabilitacao(oldFormValues => ({
				...oldFormValues,
				naturalidade:
					(consultaNaturalidades.data &&
						consultaNaturalidades.data[0]?.municipioIBGE?.descricao) ||
					'',
				codigoNaturalidade:
					(consultaNaturalidades.data &&
						Number(consultaNaturalidades.data[0]?.municipioIBGE?.id)) ||
					'',
				uf:
					(consultaNaturalidades.data &&
						consultaNaturalidades.data[0]?.municipioIBGE?.uf) ||
					'',
			}));
			setDadosPreCadastro(oldFormValues => ({
				...oldFormValues,
				naturalidade:
					(consultaNaturalidades.data &&
						consultaNaturalidades.data[0]?.municipioIBGE?.descricao) ||
					'',
				codigoNaturalidade:
					(consultaNaturalidades.data &&
						Number(consultaNaturalidades.data[0]?.municipioIBGE?.id)) ||
					'',
				uf:
					(consultaNaturalidades.data &&
						consultaNaturalidades.data[0]?.municipioIBGE?.uf) ||
					'',
			}));

			dispatch(
				consultaCidadesUFRequest(
					consultaNaturalidades.data[0].municipioIBGE.uf,
				),
			);
		}
	}, [
		consultaNaturalidades.data,
		consultaNaturalidades.status,
		dadosPreCadastroFormSave,
		dispatch,
	]);

	useEffect(() => {
		if (
			consultaPermissionarioPontuado.data &&
			consultaNomeSocialFiliacao.status === 0 &&
			dadosPreCadastro.cpf &&
			dadosPreCadastro.cpf !== ''
		) {
			dispatch(
				consultaNomeSocialFiliacaoRequest({
					cpf: consultaPermissionarioPontuado.data.cpf,
					usuario: mainframe.idUsuario,
					senha: mainframe.senhaMainFrame,
				}),
			);
		}
	}, [
		consultaNomeSocialFiliacao.status,
		consultaPermissionarioPontuado.data,
		dadosPreCadastro.cpf,
		dispatch,
		mainframe.idUsuario,
		mainframe.senhaMainFrame,
	]);

	useEffect(() => {
		if (
			!dadosPreCadastroFormSave.dataPreCadastro?.cpf &&
			consultaNomeSocialFiliacao.status === 200 &&
			consultaNomeSocialFiliacao.data
		) {
			const { nomeSocial, filiacao1, filiacao2 } =
				consultaNomeSocialFiliacao.data;

			setDadosPreCadastro(oldFormValues => ({
				...oldFormValues,
				nomeSocial: nomeSocial?.trim() || '',
				paisAfetivo1: filiacao1?.trim() || '',
				paisAfetivo2: filiacao2?.trim() || '',
				origemNomeSocial: getOriginOfSocialName(
					consultaNomeSocialFiliacao,
					null,
				),
			}));
		}
	}, [consultaNomeSocialFiliacao, dadosPreCadastroFormSave]);

	useEffect(() => {
		if (
			!dadosPreCadastroFormSave.dataPreCadastro?.cpf &&
			consultaPermissionarioPontuado.data?.cpf &&
			consultaPesquisaNomeSocialFiliacao.status === 0 &&
			((consultaNomeSocialFiliacao.status !== 0 &&
				consultaNomeSocialFiliacao.status !== 200) ||
				(consultaNomeSocialFiliacao.status === 200 &&
					!consultaNomeSocialFiliacao.data?.nomeSocial?.trim()))
		) {
			dispatch(
				consultaPesquisaNomeSocialFiliacaoRequest({
					usuario: mainframe.idUsuario,
					terminal: user.ip,
					cpf: consultaPermissionarioPontuado.data.cpf,
				}),
			);
		}
	}, [
		consultaNomeSocialFiliacao,
		consultaPermissionarioPontuado.data,
		consultaPesquisaNomeSocialFiliacao.status,
		dispatch,
		mainframe.idUsuario,
		user.ip,
		dadosPreCadastroFormSave,
	]);

	useEffect(() => {
		if (
			!dadosPreCadastroFormSave.dataPreCadastro?.cpf &&
			consultaPesquisaNomeSocialFiliacao.status === 200 &&
			consultaPesquisaNomeSocialFiliacao.data
		) {
			setDadosPreCadastro(oldData => ({
				...oldData,
				nomeSocial:
					consultaPesquisaNomeSocialFiliacao.data?.nomeSocial.trim() || '',
				origemNomeSocial: getOriginOfSocialName(
					null,
					consultaPesquisaNomeSocialFiliacao,
				),
			}));
		}
	}, [consultaPesquisaNomeSocialFiliacao, dadosPreCadastroFormSave]);

	// GET CODIGO CIRETRAN
	useEffect(() => {
		if (
			!dadosPreCadastroFormSave.dataPreCadastro?.cpf &&
			consultaPermissionarioPontuado.data &&
			consultaPermissionarioPontuado.data.endereco?.cep &&
			consultaPesquisaNomeSocialFiliacao.status !== 0 &&
			consultaPesquisaNomeSocialFiliacao.status !== 100 &&
			consultaCEP.status === 0
		) {
			dispatch(
				consultaCEPRequest({
					cep: consultaPermissionarioPontuado.data.endereco.cep,
				}),
			);
		}
	}, [
		consultaCEP.status,
		consultaPermissionarioPontuado.data,
		consultaPesquisaNomeSocialFiliacao.status,
		dadosPreCadastro.cpf,
		dispatch,
		dadosPreCadastroFormSave,
	]);

	useEffect(() => {
		if (
			consultaCEP.status === 200 &&
			consultaCEP.data &&
			!dadosPreCadastroFormSave.dataPreCadastro?.cpf
		) {
			setDadosPreCadastro(oldFormValues => ({
				...oldFormValues,
				codCiretranHabilitacao:
					consultaCEP.data?.codigoUnidadeTransito?.toString() || '',
				codCiretranCondutor:
					consultaCEP.data?.codigoUnidadeTransito?.toString() || '',
			}));
		}
	}, [consultaCEP.data, consultaCEP.status, dadosPreCadastroFormSave]);

	useEffect(() => {
		if (cadastroEnderecos.status === 200 && requestData !== null) {
			dispatch(dadosCidadaoRequest(requestData.cidadao));
		}
	}, [cadastroEnderecos.status, dispatch, requestData]);

	useEffect(() => {
		if (dadosCidadao.status === 200 && requestData !== null) {
			const tipoExame = 'AMBOS';

			const inapto = {
				cpf: requestData.cidadao?.cidadao?.cpf?.toString() || '',
				tipoExame,
				flagDeficienteFisico:
					requestData.cidadao?.cidadao?.necessidade_veiculo_adaptado || false,
			};

			setTimeout(() => {
				dispatch(consultarInaptoRequest(inapto));
			}, 300);
		}
	}, [dadosCidadao.status, dispatch, requestData]);

	useEffect(() => {
		if (consultarInapto.status !== 0 && consultarInapto.status !== 100) {
			dispatch(estatisticaIncluirClear());
			history.push(ROUTE_DETRAN_CNH_PERMISSIONARIO_PONTUADO_AGENDAMENTO_MEDICO);
		}
	}, [consultarInapto.status, dispatch, history]);

	// RECARREGA OS DADOS DO FORM COM OS DADOS SALVOS NA STORE.
	useEffect(() => {
		if (dadosPreCadastroFormSave.dataPreCadastro?.cpf) {
			setDadosPreCadastro(dadosPreCadastroFormSave.dataPreCadastro);
		}
	}, [dispatch, dadosPreCadastroFormSave]);

	// RECARREGA OS DADOS DO FORM COM OS DADOS SALVOS NA STORE.
	useEffect(() => {
		if (dadosPreCadastroFormSave.dataPreCadastroHabilitacao?.cpf) {
			setDadosHabilitacao(dadosPreCadastroFormSave.dataPreCadastroHabilitacao);
		}
	}, [dispatch, dadosPreCadastroFormSave]);

	useEffect(() => {
		clearInitialStatesWithoutNewCallServices();
		if (!dadosPreCadastroFormSave.dataPreCadastro?.cpf) {
			dispatch(consultaNomeSocialFiliacaoClear());
			dispatch(consultaPesquisaNomeSocialFiliacaoClear());
			dispatch(consultaSegundaViaReabilitacaoClear());
			dispatch(domainServiceNaturalidadesClear());
			dispatch(consultaCEPClear());
		}
	}, [
		clearInitialStatesWithoutNewCallServices,
		dispatch,
		dadosPreCadastroFormSave,
	]);

	return (
		<>
			<StatusCondutor
				schema={schema}
				dadosHabilitacao={dadosHabilitacao}
				dadosPreCadastro={dadosPreCadastro}
				handleSubmit={handleSubmit}
			/>

			<Row justify="space-between" align="middle">
				<Col>
					<ButtonVoltar
						navigate={false}
						onClick={() => history.push(ROUTE_DETRAN_CNH)}
					/>
				</Col>

				<Col>
					<ButtonImage
						type="button"
						src="outro-servico-detran"
						onClick={() => history.push(ROUTE_DETRAN_CNH)}
					/>
				</Col>
			</Row>
		</>
	);
};

export default StatusCondutorPermissionarioPontuado;
